import { type IngredientCorrection } from "@fitness-app/data-models/entities/DishIssue";
import { type Ingredient, type Nutrients } from "@fitness-app/data-models/entities/Ingredient";

export type CorrectionRow =
  | { key: string; label: string; kind: "text"; before: string; after: string }
  | { key: string; label: string; kind: "image"; before: string | null; after: string | null };

/** Always listed when a proposal touches nutrients, so the trainer sees the macros next to the changed value. */
const MAIN_NUTRIENTS = ["calories", "protein", "fat", "carbohydrates"] as const;

const GRAM_NUTRIENTS = new Set<string>([
  "protein",
  "vegetableProtein",
  "animalProtein",
  "fat",
  "carbohydrates",
  "digestibleCarbs",
  "sugars",
  "saturatedFat",
  "polyunsaturatedFat",
  "monounsaturatedFat",
  "fiber",
  "salt",
]);

const EMPTY = "-";

const TEXT_FIELD_LABELS = {
  name: "Nazwa",
  brand: "Marka",
  manufacturer: "Producent",
  rawIngredients: "Skład",
  barCode: "Kod kreskowy",
} as const;

const FLAG_LABELS = {
  isVegan: "Wegańskie",
  isVegetarian: "Wegetariańskie",
  isDairyFree: "Bez nabiału",
  isGlutenFree: "Bez glutenu",
  isHighProtein: "Wysokobiałkowe",
} as const;

const yesNo = (value: unknown): string => (value ? "Tak" : "Nie");

const formatNumber = (value: unknown, unit?: string): string => {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return EMPTY;
  }
  const formatted = String(Math.round(value * 10) / 10).replace(".", ",");
  return unit ? `${formatted} ${unit}` : formatted;
};

const formatText = (value: unknown): string => (typeof value === "string" && value.trim() ? value : EMPTY);

const formatMeasures = (measures: Ingredient["measures"] | null | undefined): string =>
  measures?.length
    ? measures.map((measure) => `${measure.name || "miara"} ${formatNumber(measure.weightPerUnit, "g")}`).join(", ")
    : EMPTY;

const formatNutrient = (key: string, value: unknown): string =>
  formatNumber(value, key === "calories" ? "kcal" : GRAM_NUTRIENTS.has(key) ? "g" : undefined);

const nutrientRows = (
  before: Partial<Nutrients> | null | undefined,
  after: Partial<Nutrients> | null | undefined,
  nutrientLabel: (key: string) => string,
): CorrectionRow[] => {
  const previous: Record<string, unknown> = before ?? {};
  const next: Record<string, unknown> = after ?? {};
  const changedKeys = Object.keys(next).filter(
    (key) => JSON.stringify(next[key] ?? null) !== JSON.stringify(previous[key] ?? null),
  );
  const keys = [
    ...MAIN_NUTRIENTS,
    ...changedKeys.filter((key) => !(MAIN_NUTRIENTS as readonly string[]).includes(key)),
  ];

  return keys.map((key) => ({
    key: `nutrients.${key}`,
    label: `${nutrientLabel(key)} (100 g)`,
    kind: "text",
    before: formatNutrient(key, previous[key]),
    after: formatNutrient(key, key in next ? next[key] : previous[key]),
  }));
};

/**
 * Before → after rows for the fields a correction changes. `before` is the product as the client saw it
 * (`metadata.before`), `changes` only the changed fields (`metadata.proposedChanges`).
 */
export const buildCorrectionRows = (
  before: IngredientCorrection,
  changes: IngredientCorrection,
  nutrientLabel: (key: string) => string,
): CorrectionRow[] => {
  const rows: CorrectionRow[] = [];

  for (const field of Object.keys(TEXT_FIELD_LABELS) as (keyof typeof TEXT_FIELD_LABELS)[]) {
    if (field in changes) {
      rows.push({
        key: field,
        label: TEXT_FIELD_LABELS[field],
        kind: "text",
        before: formatText(before[field]),
        after: formatText(changes[field]),
      });
    }
  }

  if ("image" in changes) {
    rows.push({
      key: "image",
      label: "Zdjęcie",
      kind: "image",
      before: before.image ?? null,
      after: changes.image ?? null,
    });
  }

  if ("calories" in changes && !("nutrients" in changes)) {
    rows.push({
      key: "calories",
      label: `${nutrientLabel("calories")} (100 g)`,
      kind: "text",
      before: formatNutrient("calories", before.calories),
      after: formatNutrient("calories", changes.calories),
    });
  }

  if ("nutrients" in changes) {
    rows.push(...nutrientRows(before.nutrients, changes.nutrients, nutrientLabel));
  }

  if ("packageSize" in changes) {
    rows.push({
      key: "packageSize",
      label: "Wielkość opakowania",
      kind: "text",
      before: formatNumber(before.packageSize, "g"),
      after: formatNumber(changes.packageSize, "g"),
    });
  }

  if ("measures" in changes) {
    rows.push({
      key: "measures",
      label: "Miary",
      kind: "text",
      before: formatMeasures(before.measures),
      after: formatMeasures(changes.measures),
    });
  }

  if ("liquid" in changes) {
    rows.push({
      key: "liquid",
      label: "Płyn",
      kind: "text",
      before: yesNo(before.liquid),
      after: yesNo(changes.liquid),
    });
  }

  if ("unit" in changes) {
    const basis = (unit: unknown) => (unit === "ml" ? "100 ml" : unit ? "100 g" : EMPTY);
    rows.push({
      key: "unit",
      label: "Wartości na",
      kind: "text",
      before: basis(before.unit),
      after: basis(changes.unit),
    });
  }

  if ("mainCategory" in changes) {
    rows.push({
      key: "mainCategory",
      label: "Kategoria",
      kind: "text",
      before: before.mainCategory == null ? EMPTY : String(before.mainCategory),
      after: changes.mainCategory == null ? EMPTY : String(changes.mainCategory),
    });
  }

  for (const flag of Object.keys(FLAG_LABELS) as (keyof typeof FLAG_LABELS)[]) {
    if (flag in changes) {
      rows.push({
        key: flag,
        label: FLAG_LABELS[flag],
        kind: "text",
        before: yesNo(before[flag]),
        after: yesNo(changes[flag]),
      });
    }
  }

  return rows;
};
